import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MasterDataApi } from './api';
import type {
  BatchMasterDataRequest,
  CreateMasterDataRowDto,
  MasterDataListParams,
  UpdateMasterDataRowDto,
} from './types';

const QUERY_KEYS = {
  all: ['master-data'] as const,
  list: (orgId: string, params?: MasterDataListParams) =>
    [...QUERY_KEYS.all, orgId, 'list', params] as const,
  datasets: (orgId: string) =>
    [...QUERY_KEYS.all, orgId, 'datasets'] as const,
};

export const useMasterDataList = (orgId: string, params?: MasterDataListParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: QUERY_KEYS.list(orgId, params),
    queryFn: () => MasterDataApi.list(orgId, params),
    enabled: !!orgId && (options?.enabled ?? true),
  });
};

export const useMasterDataDatasets = (orgId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.datasets(orgId),
    queryFn: () => MasterDataApi.listDatasets(orgId),
    enabled: !!orgId,
  });
};

export const useCreateMasterDataRow = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateMasterDataRowDto) => MasterDataApi.create(orgId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useUpdateMasterDataRow = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId, dto }: { rowId: number; dto: UpdateMasterDataRowDto }) =>
      MasterDataApi.update(orgId, rowId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useDeleteMasterDataRow = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rowId: number) => MasterDataApi.remove(orgId, rowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useImportMasterData = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dataset, file }: { dataset: string; file: File }) =>
      MasterDataApi.importCsv(orgId, dataset, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useBatchMasterData = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: BatchMasterDataRequest) => MasterDataApi.batch(orgId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export { QUERY_KEYS as masterDataQueryKeys };
